// Light and Dark theme configurations

export const darkTheme = {
  mode: 'dark',
  colors: {
    // Primary Gradient - Electric purple to violet
    primaryGradientStart: '#7C3AED',
    primaryGradientEnd: '#A78BFA',

    // Accent Colors
    accentPurple: '#8B5CF6',
    accentCyan: '#06B6D4',
    hoverVariant: '#A855F7',
    secondaryAccent: '#C4B5FD',

    // Dark Mode Neutrals
    background: '#0F0F23',
    backgroundSecondary: '#1A1A2E',
    backgroundTertiary: '#16213E',
    surfaceCard: 'rgba(26, 26, 46, 0.8)',
    surfaceCardSolid: '#1A1A2E',
    textLight: '#F8FAFC',
    textDark: '#E2E8F0',
    subtextGray: '#94A3B8',
    borderSubtle: 'rgba(139, 92, 246, 0.2)',

    // Status Colors
    onlineGreen: '#22C55E',
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B',
  },

  gradients: {
    primary: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    primaryVertical: 'linear-gradient(180deg, #7C3AED 0%, #A78BFA 100%)',
    subtle: 'linear-gradient(135deg, rgba(124, 58, 237, 0.15) 0%, rgba(167, 139, 250, 0.15) 100%)',
    glow: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0) 70%)',
    cyanGlow: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, rgba(6, 182, 212, 0) 70%)',
    dark: 'linear-gradient(180deg, #0F0F23 0%, #1A1A2E 100%)',
    cardGlass: 'linear-gradient(135deg, rgba(26, 26, 46, 0.9) 0%, rgba(22, 33, 62, 0.8) 100%)',
    heroBackground: 'radial-gradient(ellipse at 50% 0%, rgba(124, 58, 237, 0.15) 0%, transparent 50%)',
  },

  shadows: {
    small: '0 2px 8px rgba(0, 0, 0, 0.3)',
    medium: '0 8px 24px rgba(0, 0, 0, 0.4)',
    large: '0 16px 48px rgba(0, 0, 0, 0.5)',
    glow: '0 0 40px rgba(139, 92, 246, 0.4)',
    glowCyan: '0 0 30px rgba(6, 182, 212, 0.3)',
    card: '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(139, 92, 246, 0.1)',
    cardHover: '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 40px rgba(139, 92, 246, 0.2)',
  },
};

export const lightTheme = {
  mode: 'light',
  colors: {
    // Primary Gradient - Same purple tones
    primaryGradientStart: '#7C3AED',
    primaryGradientEnd: '#A78BFA',

    // Accent Colors
    accentPurple: '#8B5CF6',
    accentCyan: '#0891B2',
    hoverVariant: '#A855F7',
    secondaryAccent: '#C4B5FD',

    // Light Mode Neutrals
    background: '#FAFAFA',
    backgroundSecondary: '#F5F5F5',
    backgroundTertiary: '#EFEFEF',
    surfaceCard: 'rgba(255, 255, 255, 0.9)',
    surfaceCardSolid: '#FFFFFF',
    textLight: '#1F2937',
    textDark: '#374151',
    subtextGray: '#6B7280',
    borderSubtle: 'rgba(139, 92, 246, 0.15)',

    // Status Colors
    onlineGreen: '#22C55E',
    success: '#10B981',
    error: '#EF4444',
    warning: '#F59E0B',
  },

  gradients: {
    primary: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
    primaryVertical: 'linear-gradient(180deg, #7C3AED 0%, #A78BFA 100%)',
    subtle: 'linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(167, 139, 250, 0.08) 100%)',
    glow: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0) 70%)',
    cyanGlow: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0) 70%)',
    dark: 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)',
    cardGlass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 250, 0.9) 100%)',
    heroBackground: 'radial-gradient(ellipse at 50% 0%, rgba(124, 58, 237, 0.08) 0%, transparent 50%)',
  },

  shadows: {
    small: '0 2px 8px rgba(0, 0, 0, 0.08)',
    medium: '0 8px 24px rgba(0, 0, 0, 0.1)',
    large: '0 16px 48px rgba(0, 0, 0, 0.12)',
    glow: '0 0 40px rgba(139, 92, 246, 0.2)',
    glowCyan: '0 0 30px rgba(6, 182, 212, 0.15)',
    card: '0 4px 24px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(139, 92, 246, 0.08)',
    cardHover: '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 40px rgba(139, 92, 246, 0.1)',
  },
};

// Shared theme values (don't change between modes)
export const sharedTheme = {
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    weights: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },

  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
    xxl: '4rem',
    xxxl: '6rem',
  },

  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1440px',
  },

  transitions: {
    fast: '0.2s ease',
    medium: '0.3s ease',
    slow: '0.5s ease',
    spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
};

// Merge function to combine mode theme with shared values
export const getTheme = (mode = 'dark') => {
  const modeTheme = mode === 'light' ? lightTheme : darkTheme;
  return {
    ...sharedTheme,
    ...modeTheme,
  };
};

// Default export for backwards compatibility
export const theme = getTheme('dark');
